import { cn } from "@newxsystems/shared";
import { KaajAccordion } from "../../../../modules/interactive/components";
import { getKaajSectionSpacing } from "../../../../modules/layout/kaaj-section-spacing";
import type { IAccordionBlockProps } from "./AccordionBlock.props";
import { RichTextHtml } from "../../RichTextHtml";
import { KaajAction } from "../_shared/KaajAction";

export const AccordionBlock = ({
  anchorId,
  buttons,
  direction,
  eyebrow,
  heading,
  items,
  mode,
  parentClass,
  spacing,
  subheading,
  variant,
}: IAccordionBlockProps) => {
  if (!items?.length) {
    return null;
  }

  const isHorizontal = variant === "horizontal";
  const headingId = heading && anchorId ? `${anchorId}-heading` : undefined;

  return (
    <section
      id={anchorId ?? undefined}
      aria-labelledby={headingId}
      aria-label={!headingId && heading ? heading : undefined}
      className={cn(
        "relative isolate overflow-hidden",
        getKaajSectionSpacing(spacing),
        parentClass,
      )}
      data-kaaj-accordion-variant={variant}
      data-kaaj-accordion-spacing={spacing ?? undefined}
    >
      <div
        className={cn(
          "kaaj-container relative z-10",
          isHorizontal
            ? cn(
                "grid gap-8 md:gap-10 lg:items-start lg:gap-12",
                direction === "ltr"
                  ? "lg:grid-cols-[minmax(0,1.18fr)_minmax(0,0.82fr)]"
                  : "lg:grid-cols-[minmax(0,0.82fr)_minmax(0,1.18fr)]",
              )
            : "space-y-9 sm:space-y-10",
        )}
      >
        <div
          className={cn(
            "flex min-w-0 flex-col",
            isHorizontal ? "text-left lg:pt-3" : "mx-auto items-center text-center",
            isHorizontal && direction === "ltr" ? "order-1 lg:order-2" : undefined,
          )}
        >
          {eyebrow ? (
            <p className="mb-5 inline-flex w-fit max-w-full rounded-full border border-[#CAD7FF] bg-white px-3 py-1 text-xs font-extrabold uppercase tracking-[0.14em] text-[#2144FF] shadow-[0_10px_26px_rgba(33,68,255,0.08)] [overflow-wrap:anywhere]">
              {eyebrow}
            </p>
          ) : null}
          {heading ? (
            <h2 id={headingId} className="kaaj-section-heading text-[#151927]">
              {heading}
            </h2>
          ) : null}
          {subheading ? (
            <RichTextHtml
              className="mt-5 text-pretty text-base leading-7 text-[#405076] [overflow-wrap:anywhere] sm:text-lg sm:leading-8"
              html={subheading}
            />
          ) : null}
          {buttons?.length ? (
            <div className="mt-7 flex flex-wrap gap-3">
              {buttons.map((button, index) => (
                <KaajAction
                  ariaLabel={button.ariaLabel}
                  href={button.href}
                  key={`${button.href}-${button.text}-${index}`}
                  label={button.text}
                  target={button.target}
                  variant={button.style}
                />
              ))}
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            "min-w-0 w-full",
            isHorizontal && direction === "ltr" ? "order-2 lg:order-1" : undefined,
          )}
        >
          <KaajAccordion
            items={items}
            mode={mode}
            direction={direction}
            presentation={isHorizontal ? "cards" : "ledger"}
            ariaLabel={heading ?? undefined}
          />
        </div>
      </div>
    </section>
  );
};

AccordionBlock.displayName = "Block::AccordionBlock";
